import { Helpers } from '../core/src/helpers';
import SterlingAPIClient, { SterlingAPI } from '../core/src/sterling-api';
import { EtrAPI } from '../core/src/etr-api-client';

import { formatMetricValue, getDerivedProductMetricsV2 } from '../metrics/metrics-helpers';

const DEFAULT_LEAD_TIME = 14;

/**
 * Checks if two arrays of strings have equal content
 * @param {*} a
 * @param {*} b
 * @returns
 */
const isStrArrayEqual = (a, b) => {
    try {
        return a.join('') === b.join('');
    } catch (e) {}
};

const getTotalGMPoints = (selectedSKUs, GMPoints, scale = 0) => {
    const gmPoints = Object.keys(GMPoints || {});
    if (gmPoints.length < 1) {
        return undefined;
    }
    try {
        return selectedSKUs
            .reduce((r, sku) => {
                //sum gm points, ignoring the entry for the base config
                const gmPoint = GMPoints[sku] || 0;
                return r + gmPoint;
            }, scale)
            .toFixed(2);
    } catch (e) {}
};

export const getSelectedRecommendedConfig = recommendedConfigs => {
    // recommended-configuration component hides the buttons when there's only 1 config
    let configs = Object.values(recommendedConfigs || {});
    if (configs.length < 2) return null;

    let { configName } =
        configs.find(config => {
            let { selected } = config || {};
            return selected === 'yes';
        }) || {};

    return configName ? formatMetricValue(configName) : null;
};
/**
 * Returns the relevant values from the property array
 * @param {} property
 */
const pickProperties = property => {
    return property.reduce((r, p) => {
        const { name, value } = p;
        r[name] = value;
        return r;
    }, {});
};

/**
 * Returns config option by path
 * @param {*} optionArray
 * @param {*} path
 * @returns
 */
const getByPath = (optionArray, path) => {
    return optionArray.find(o => o.path === path);
};

/**
 * Avoid recreating a new API instance on every pick as long as the sterling URL is the same
 */
const getAPIClient = Helpers.memoize(sterlingRestURL => {
    return sterlingRestURL ? new SterlingAPI(sterlingRestURL) : SterlingAPIClient;
});

/**
 * Only check the stock status of the derived SKU if it has changed or if the stock status is unknown. This avoids unnecessary API calls to sterling for every pick change.
 * @param {*} pickDerivedSKU
 * @returns {Promise<boolean>}
 */
const getDerivedSkuStockStatus = (pickDerivedSKU, apiClient = EtrAPI) => {
    if (!pickDerivedSKU) {
        return Promise.resolve(false);
    }
    return apiClient.cart
        .getCTOServices(undefined, [pickDerivedSKU], true)
        .then(data => {
            const { prices = {} } = data || {};
            const price = prices[pickDerivedSKU] || {};
            return price.noStock !== true;
        })
        .catch(e => {
            return false;
        });
};

/**
 * Make request to sterling API with picks to verify. Mutates baseConfig with changes
 * @param {*} components
 * @param {Function} updateComponent callback that provides updated component data
 * @param {*} options object containing optional depdency injections and callbacks
 * @returns
 */
export const verifyPicks = (components, updateComponent, options = {}) => {
    const { etrAPI = EtrAPI, onPriceReady } = options;
    const { baseConfig, isCallCenter } = components;
    if (!baseConfig) {
        return Promise.reject('Components missing baseConfig for CTO');
    }
    const { path, specCategories, catentryId, sterlingRestURL, _dsku, sku: parentSku, tabs = [] } = baseConfig;
    const { pick, skuList, crossSellSkus, crossSellPicks } = getPicks(baseConfig);
    const sterlingAPI = getAPIClient(sterlingRestURL);
    return Promise.all([
        sterlingAPI.cto.processConfigurationPicks({
            path,
            picks: {
                //include cross sell picks in the request to properly update selected state
                pick: pick.concat(crossSellPicks),
            },
        }),
        etrAPI.ctoServlet.getLeadTime(parentSku, skuList).catch(r => null),
    ]).then(async ([resp, leadTimeOverride]) => {
        const { optionclass, messages, property } = resp;
        let leadTime = 1;
        let hasLeadTime = false;
        const selectedSKUs = [];
        const hiddenSKUs = [];
        const { _dsku: pickDerivedSKU } = pickProperties(property);
        let diff = false;
        //build map of messages by part PATH
        let {
            error: errorMap,
            suggestion: infoMap,
            warning: warningMap,
        } = Object.keys(messages).reduce((mapResult, key) => {
            const messageList = messages[key];
            mapResult[key] = (messageList || []).reduce((r, e) => {
                const { path, message } = e;
                r[path] = r[path] || [];
                r[path].push(message);
                return r;
            }, {});
            return mapResult;
        }, {});
        /* 
          update _dsku if received from verify picks, if no value MUST default to empty string 
          check for dsku inventory if dsku changed or if dsku is present and inventory status is unknown
          */
        if (pickDerivedSKU !== _dsku || (_dsku && typeof baseConfig.dSkuInStock !== 'boolean')) {
            const dSkuInStock = await getDerivedSkuStockStatus(pickDerivedSKU, etrAPI);
            baseConfig.dSkuInStock = dSkuInStock;
            if (dSkuInStock) {
                diff = true;
                baseConfig._dsku = pickDerivedSKU || '';
            } else if (baseConfig._dsku && !dSkuInStock) {
                diff = true;
                baseConfig._dsku = '';
            }
        }

        //check response and apply any differences
        optionclass.forEach(oc => {
            const { property, path, optionitem } = oc;
            const { _isViewable, ['UI: POST PICK GUIDING TEXT']: guidingText } = pickProperties(property);
            const specOption = getByPath(specCategories, path);
            const shouldDisplay =
                _isViewable === '1' && !!optionitem && (tabs.length < 1 || tabs.includes(path)) ? 'true' : 'false';
            if (!specOption) {
                return;
            }
            //never display ShipDate
            if (/Shipping_ESD.ShipDate$/.test(path)) {
                specOption.display = 'false';
            } else if (specOption.display !== shouldDisplay) {
                //update option based on response
                diff = true;
                specOption.display = shouldDisplay;
            }
            //set errorText
            if (!isStrArrayEqual(specOption.errorText, errorMap[path])) {
                diff = true;
                specOption.errorText = errorMap[path];
            }
            //set infoText
            if (!isStrArrayEqual(specOption.infoText, infoMap[path])) {
                diff = true;
                specOption.infoText = infoMap[path];
            }
            //TODO: not sure if guiding text needs to be stored separately
            if (specOption.guidingText !== guidingText) {
                try {
                    diff = true;
                    specOption.guidingText = (guidingText || '').replace(/\&nbsp\;/gi, '');
                } catch (e) {}
            }

            //set warningText
            if (!isStrArrayEqual(specOption.warningText, warningMap[path])) {
                diff = true;
                specOption.warningText = warningMap[path];
            }

            if (!optionitem) return;

            //update option items based on response
            (optionitem || []).forEach((oi, idx) => {
                const { property, path } = oi;
                const { _quantity, _isViewable, ['UI: LEAD TIME']: newLeadTime } = pickProperties(property);
                const specOptionItem = getByPath(specOption.optionitems, path);
                const shouldDisplay = _isViewable === '1' ? 'true' : 'false';
                const isSelected = _quantity === '1' ? 'true' : 'false';
                if (!specOptionItem) {
                    return;
                }
                if (isSelected === 'true') {
                    //exclude cross sell skus from selectedSKUs, they will be sent in a separate crossSell parameter
                    specOptionItem.sku && specOption.crossSell !== 'true' && selectedSKUs.push(specOptionItem.sku);
                    //track selected hidden skus to remove from seo update list
                    specOptionItem.sku &&
                        specOption.crossSell !== 'true' &&
                        (shouldDisplay === 'false' || specOption.display === 'false') &&
                        hiddenSKUs.push(specOptionItem.sku);
                    if (newLeadTime * 1 > leadTime) {
                        leadTime = newLeadTime;
                        hasLeadTime = true;
                    }
                    //for color options set the selected color image if available
                    if (oc.name === 'Color' && baseConfig.alternateImage !== specOptionItem.image) {
                        baseConfig.alternateImage = specOptionItem.image;
                        diff = true;
                    }
                }
                //update option item based on response
                if (specOptionItem.display !== shouldDisplay) {
                    diff = true;
                    specOptionItem.display = shouldDisplay;
                }
                if (specOptionItem.selected !== isSelected) {
                    diff = true;
                    specOptionItem.selected = isSelected;
                }
                if (specOptionItem.leadTime !== newLeadTime) {
                    diff = true;
                    specOptionItem.leadTime = newLeadTime;
                }
                //set errorText
                if (!isStrArrayEqual(specOptionItem.errorText, errorMap[path])) {
                    diff = true;
                    specOptionItem.errorText = errorMap[path];
                }
                //set infoText
                if (!isStrArrayEqual(specOptionItem.infoText, infoMap[path])) {
                    diff = true;
                    specOptionItem.infoText = infoMap[path];
                }
                //set warningText
                if (!isStrArrayEqual(specOptionItem.warningText, warningMap[path])) {
                    diff = true;
                    specOptionItem.warningText = warningMap[path];
                }
            });
        });
        if (diff) {
            updateComponent && updateComponent({ ...components });
        }
        //if no lead time set at the option item level then set leadTime to default
        if (!hasLeadTime) {
            leadTime = DEFAULT_LEAD_TIME;
        }
        //update shipdate after pick verification, if dsku is present leadtime is always 1 (STO product)
        leadTime = !!baseConfig._dsku ? 1 : leadTimeOverride || leadTime;
        //clear out errors if none returned
        const errors = Object.keys(errorMap).length > 0 ? errorMap : null;
        Promise.all([
            sterlingAPI.cto.getShipDate(leadTime).catch(e => {
                return {};
            }),
            etrAPI.cart.getCTOPrice(catentryId, selectedSKUs, isCallCenter && 'CC_CSR', crossSellSkus.join(',')),
        ]).then(([shipDateData, priceData]) => {
            const { shipDate } = shipDateData;
            const {
                PromPrice: salePrice,
                BasePrice: regularPrice,
                sliderPromo,
                error,
                GMPoints,
                scale,
                ctoConfiguration,
            } = priceData;
            const ctoPrice = {
                salePrice,
                regularPrice,
                sliderPromo,
                currency: 'USD',
                locale: 'us-EN',
                error: error || null,
                gmPoints: getTotalGMPoints(selectedSKUs, GMPoints, scale),
                ...(ctoConfiguration !== undefined && { ctoConfiguration }),
            };
            const { discount, discountPercent } = Helpers.getPriceDiscount(ctoPrice);
            ctoPrice.priceDifference = discount;
            ctoPrice.discountPercent = discountPercent;
            typeof onPriceReady === 'function' && onPriceReady(ctoPrice);
            if (updateComponent) {
                updateComponent(
                    {
                        shipDate: {
                            date: shipDate,
                            leadTime,
                            days: Helpers.date.getDayDifference(shipDate, new Date()),
                        },
                        ctoPrice,
                        GMPoints,
                        errors,
                        verifyingPicks: false,
                        //store skuList of picks to use with fps calculator
                        skuList,
                        //component list, used for seo update - only include visible components
                        componentList: selectedSKUs
                            .filter(sku => hiddenSKUs.indexOf(sku) === -1)
                            ?.sort((a, b) => a.localeCompare(b)),
                    },
                    (state, components) => {
                        //lodash merge causes strange behaviors when merging arrays, manually merge components
                        const { components: stateComponents } = state;
                        return { components: { ...stateComponents, ...components } };
                    },
                );
            }
        });
    });
};

/**
 * Build pick object
 * @param {*} baseConfig - baseConfig object for the CTO
 * @param {Boolean} excludeCrossSell - excludes selected options that are cross sells
 * @returns
 */
export const getPicks = baseConfig => {
    const { specCategories } = baseConfig;
    return specCategories.reduce(
        (agg, specCat) => {
            const { pick, skuPrice, crossSellPicks, crossSellSkus, skuList } = agg;
            const selectedPrices = [];
            const { crossSell, optionitems } = specCat;
            const isCrossSell = crossSell === 'true';
            const selectedOptions = optionitems.reduce((totalSelected, option) => {
                const { selected, sku, path, price } = option;
                if (selected === 'true' && sku) {
                    if (isCrossSell) {
                        crossSellSkus.push(sku);
                    }
                    totalSelected.push({
                        item: path,
                        quantity: '1',
                    });
                    selectedPrices.push({
                        sku,
                        price: price.replace(/[,|\(|\)]/gi, ''),
                    });
                    skuList.push(sku);
                }
                return totalSelected;
            }, []);
            if (isCrossSell) {
                agg.crossSellPicks = crossSellPicks.concat(selectedOptions);
            } else {
                agg.pick = pick.concat(selectedOptions);
                agg.skuPrice = skuPrice.concat(selectedPrices);
            }
            return agg;
        },
        { pick: [], skuPrice: [], crossSellPicks: [], crossSellSkus: [], skuList: [] },
    );
};

/**
 * Loops through recommended configs are mark them as selected no
 * @param {*} recommendedConfigs
 * @returns
 */
export const deselectRecommendConfig = recommendedConfigs => {
    try {
        Object.keys(recommendedConfigs).forEach(k => {
            const config = recommendedConfigs[k];
            if (config && config.selected === 'yes') {
                config.selected = 'no';
            }
        });
    } catch (e) {
        //log exception
    }
    return recommendedConfigs;
};

/**
 * Mark the provided config id as selected and mark the rest as not selected
 * @param {*} specCategories
 * @param {*} recommendedConfigs
 * @param {*} configCatentryId
 * @returns
 */
export const selectRecommendedConfig = (specCategories, recommendedConfigs, configCatentryId) => {
    try {
        let selectedConfig;
        Object.keys(recommendedConfigs).forEach(k => {
            const config = recommendedConfigs[k];
            if (config && config.configCatentryId === configCatentryId) {
                selectedConfig = config;
                config.selected = 'yes';
            } else if (config.selected === 'yes') {
                config.selected = 'no';
            }
        });
        if (selectedConfig && selectedConfig.skuList && selectedConfig.skuList.length > 0) {
            const skuList = selectedConfig.skuList.slice();
            //go through all options and mark check from skuList
            specCategories.forEach(specCat => {
                const { optionitems } = specCat;
                optionitems.forEach(option => {
                    const skuIndex = skuList.indexOf(option.sku);
                    if (skuIndex > -1) {
                        skuList.splice(skuIndex, 1);
                        option.selected = 'true';
                    } else {
                        option.selected = 'false';
                    }
                });
            });
        }
    } catch (e) {
        //log exception
    }
    return recommendedConfigs;
};

export const getRecommendedConfig = recommendedConfigs => {
    const { ctaCompareLink, ctaCompareText, ...configs } = recommendedConfigs || {};
    let recConfig;
    const configKeys = Object.keys(configs || {});
    for (let i = 0; i < configKeys.length; i++) {
        const key = configKeys[i];
        const config = configs[key];
        if (config && config.selected === 'yes') {
            recConfig = config;
            break;
        }
    }
    return recConfig;
};

export const getRecommendedConfigList = recommendedConfigs => {
    if (!recommendedConfigs) {
        return [];
    }
    const { ctaCompareLink, ctaCompareText, ...configs } = recommendedConfigs || {};
    return Object.keys(configs || {}).reduce((r, c, idx) => {
        if (!isNaN(c * 1)) {
            r.push(configs[c]);
        }
        return r;
    }, []);
};

export const trackConfiguratorChange = ({
    baseConfig,
    updatedSpec,
    selectedSpecName,
    ctoProductInitial,
    trackCustomMetric,
    ctoPrice,
    prices,
}) => {
    try {
        const componentCategory = formatMetricValue(updatedSpec.title);
        const { PromPrice: salePrice, BasePrice: regularPrice } = ctoPrice || {};
        const componentSelection = formatMetricValue(selectedSpecName);

        const ctoProduct = ctoProductInitial
            ? ctoProductInitial
            : baseConfig
              ? {
                    name: baseConfig.title,
                    sku: baseConfig.sku,
                }
              : {};
        const derivedProductMetrics = getDerivedProductMetricsV2(ctoProduct, prices); //prices is only used to get facet_subbrand and plcode, not the actual price
        // **Analytics team feedback: Don't include the configuration in the event. Uncomment if this is needed later on.
        // const {sku, title, catentryId} = baseConfig;
        // const {optionitems} = updatedSpec || {};
        // const {specCategories} = baseConfig || {};
        // const fullConfiguration = (specCategories || []).reduce((allConfig, nextConfig) => {
        //     try{
        //         let {optionitems, title} = nextConfig || {};
        //         optionitems.forEach(opt => {
        //             let {name, selected} = opt || {};
        //             if(selected === 'true'){
        //                 allConfig.push(`${title}:${name}`);
        //             }
        //         });
        //     }catch(e){}

        //     return allConfig;
        // }, []);
        trackCustomMetric('configuratorChange', {
            componentCategory,
            componentSelection,
            // configuration: fullConfiguration.join(','), // Not included - Analytic team's request
            products: [
                {
                    ...derivedProductMetrics,
                    price: salePrice || regularPrice,
                },
            ],
            list: 'Configurator Change',
        });
    } catch (e) {}
};
